import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isSidebarPathActive } from './isSidebarPathActive';
import type { SidebarDepartment, SidebarNavLeaf } from './sidebarMenuTypes';
import { flattenSidebarNavPaths } from './sidebarMenuTypes';
import { SidebarNavBranch } from './SidebarNavBranch';
import { SidebarNavItem } from './SidebarNavItem';

type SidebarDepartmentGroupProps = {
  department: SidebarDepartment;
  collapsed?: boolean;
  onNavigate?: () => void;
};

function departmentHasActiveRoute(department: SidebarDepartment, pathname: string): boolean {
  const siblingPaths = flattenSidebarNavPaths(department.items);
  return siblingPaths.some((path) => isSidebarPathActive(pathname, path, siblingPaths));
}

export function SidebarDepartmentGroup({
  department,
  collapsed = false,
  onNavigate,
}: SidebarDepartmentGroupProps) {
  const location = useLocation();
  const isActiveGroup = departmentHasActiveRoute(department, location.pathname);
  const [expanded, setExpanded] = useState(isActiveGroup);
  const siblingPaths = flattenSidebarNavPaths(department.items);

  useEffect(() => {
    if (isActiveGroup) {
      setExpanded(true);
    }
  }, [isActiveGroup]);

  const DepartmentIcon = department.icon;

  function renderItem(item: SidebarNavLeaf) {
    if (item.children && item.children.length > 0) {
      return (
        <SidebarNavBranch
          key={item.code}
          item={item}
          siblingPaths={siblingPaths}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />
      );
    }

    return (
      <SidebarNavItem
        key={item.code}
        to={item.to}
        label={item.label}
        icon={item.icon}
        collapsed={collapsed}
        onNavigate={onNavigate}
        siblingPaths={siblingPaths}
      />
    );
  }

  if (collapsed) {
    return (
      <div className="space-y-1">
        <button
          type="button"
          title={department.label}
          aria-label={department.label}
          onClick={() => setExpanded((current) => !current)}
          className={`flex w-full items-center justify-center rounded-xl px-2 py-2.5 transition-colors ${
            isActiveGroup
              ? 'bg-white/15 text-white'
              : 'text-primary-100 hover:bg-white/10 hover:text-white'
          }`}
        >
          <DepartmentIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        </button>
        {expanded ? department.items.map((item) => renderItem(item)) : null}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((current) => !current)}
        className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
          isActiveGroup
            ? 'bg-white/10 text-white'
            : 'text-primary-50 hover:bg-white/10 hover:text-white'
        }`}
      >
        <DepartmentIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate text-left">{department.label}</span>
        <ChevronDownIcon
          className={`h-4 w-4 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {expanded ? (
        <div className="ml-2 space-y-0.5 border-l border-white/15 pl-2">
          {department.items.map((item) => renderItem(item))}
        </div>
      ) : null}
    </div>
  );
}
